import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import axios from "axios";
import {
  roomsUrl, roomsBulkUrl, roomByIdUrl, roomStatusUrl,
  roomTypesUrl, roomTypeByIdUrl,
  uploadRoomImageUrl, ASSETS,
} from "../../BaseURL";
// Amenity catalogue lives in utils/ so it's shared across screens
// (Rooms, Check In / Out, Reservations) without duplication.
import { AMENITY_OPTIONS, AMENITY_BY_KEY, parseAmenities } from "../../utils/amenities";

// ── Status catalogue ────────────────────────────────────────────────────────
// The backend ENUM is the source of truth — these are the matching display
// definitions used everywhere on the page (filter pills, tiles, badges).
const STATUSES = [
  { key:"available",    label:"Available",    dot:"#4a7c59", bg:"rgba(74,124,89,.10)",  color:"#2d6a4f", border:"1px solid rgba(74,124,89,.25)" },
  { key:"occupied",     label:"Occupied",     dot:"#c8a05a", bg:"#2c1f14",              color:"#e7c98a", border:"1px solid rgba(200,160,90,.35)" },
  { key:"reserved",     label:"Reserved",     dot:"#2980b9", bg:"rgba(41,128,185,.10)", color:"#1a5a8a", border:"1px solid rgba(41,128,185,.25)" },
  { key:"cleaning",     label:"Cleaning",     dot:"#d4a017", bg:"rgba(212,160,23,.10)", color:"#8a6a14", border:"1px solid rgba(212,160,23,.28)" },
  { key:"maintenance",  label:"Maintenance",  dot:"#e67e22", bg:"rgba(230,126,34,.10)", color:"#a85618", border:"1px solid rgba(230,126,34,.25)" },
  { key:"out_of_order", label:"Out of Order", dot:"#c0392b", bg:"rgba(192,57,43,.10)",  color:"#922b21", border:"1px solid rgba(192,57,43,.25)" },
];
const STATUS_BY_KEY = Object.fromEntries(STATUSES.map(s => [s.key, s]));

const BED_TYPES = ["Single", "Double", "Queen", "King", "Twin", "Bunk", "Sofa Bed"];
const VIEW_TYPES = ["Sea", "Ocean", "Garden", "Pool", "City", "Courtyard", "Mountain", "Standard"];

const EMPTY_ROOM = {
  room_number: "", room_type_id: "", floor: "",
  price_per_night: "", max_occupancy: 2,
  bed_type: "", size_sqm: "", view_type: "",
  description: "", amenities: [], image_url: "",
  status: "available", notes: "",
  // ── Bulk-create only.  Stripped before the single-room POST. ──
  bulk: false, bulk_start: "", bulk_count: 1,
};

// Generate a sequence of room numbers from a "start" string + count.
//   "201"     + 3 → ["201","202","203"]
//   "G05"     + 3 → ["G05","G06","G07"]      (zero-padding preserved)
//   "VVIP-1"  + 3 → ["VVIP-1","VVIP-2","VVIP-3"]
//   "Suite"   + 3 → ["Suite 1","Suite 2","Suite 3"]  (no digits → append)
function generateRoomNumbers(start, count) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const s = String(start || "").trim();
  if (!s || n < 1) return [];
  const match = s.match(/^(.*?)(\d+)$/);
  if (!match) {
    return Array.from({ length: n }, (_, i) => `${s} ${i + 1}`);
  }
  const [, prefix, num] = match;
  const startN = parseInt(num, 10);
  const width  = num.length;
  return Array.from({ length: n }, (_, i) =>
    `${prefix}${String(startN + i).padStart(width, "0")}`
  );
}

const EMPTY_TYPE = { name:"", description:"", base_price:"", hourly_rate:"", daily_rate:"", base_occupancy:2 };

// parseAmenities imported above from utils/amenities.

export default function Rooms() {
  const user    = JSON.parse(localStorage.getItem("user") || "null");
  const isAdmin = user?.role === "super_admin";

  // ── Data ────────────────────────────────────────────────────────────────
  const [rooms,        setRooms]        = useState([]);
  const [types,        setTypes]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [loadError,    setLoadError]    = useState("");

  // ── UI state ────────────────────────────────────────────────────────────
  const [view,           setView]           = useState("grid");        // grid | list
  const [statusFilter,   setStatusFilter]   = useState("all");
  const [typeFilter,     setTypeFilter]     = useState("all");
  const [floorFilter,    setFloorFilter]    = useState("all");
  const [search,         setSearch]         = useState("");
  const [selectedRoom,   setSelectedRoom]   = useState(null);          // detail panel
  const [editorOpen,     setEditorOpen]     = useState(false);
  const [editorRoom,     setEditorRoom]     = useState(EMPTY_ROOM);    // form state
  const [typeMgrOpen,    setTypeMgrOpen]    = useState(false);
  const [confirmDelete,  setConfirmDelete]  = useState(null);          // { id, room_number }
  const [msg,            setMsg]            = useState({ type:"", text:"" });
  const [saving,         setSaving]         = useState(false);
  const [uploading,      setUploading]      = useState(false);

  const fileInputRef = useRef(null);

  // ── Loaders ─────────────────────────────────────────────────────────────
  const showMsg = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type:"", text:"" }), 4000);
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [rRes, tRes] = await Promise.all([
        axios.get(roomsUrl),
        axios.get(roomTypesUrl),
      ]);
      setRooms(rRes.data?.results || []);
      setTypes(tRes.data?.results || []);
    } catch (err) {
      console.error("Rooms load error:", err);
      setLoadError(
        err.response?.status === 401
          ? "Session expired. Please log in again."
          : "Could not load rooms from the server."
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  // ── Derived ─────────────────────────────────────────────────────────────
  const floors = useMemo(() => {
    const set = new Set(rooms.map(r => r.floor).filter(Boolean));
    return Array.from(set).sort();
  }, [rooms]);

  const filteredRooms = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rooms.filter(r => {
      if (statusFilter !== "all" && r.status !== statusFilter) return false;
      if (typeFilter   !== "all" && Number(r.room_type_id) !== Number(typeFilter)) return false;
      if (floorFilter  !== "all" && r.floor !== floorFilter) return false;
      if (!term) return true;
      return (
        (r.room_number || "").toLowerCase().includes(term) ||
        (r.room_type_name || "").toLowerCase().includes(term) ||
        (r.view_type || "").toLowerCase().includes(term) ||
        (r.bed_type || "").toLowerCase().includes(term)
      );
    });
  }, [rooms, statusFilter, typeFilter, floorFilter, search]);

  // Status counts power the stat strip — kept on the *full* list, not the
  // filtered one, so the strip is a stable inventory overview.
  const counts = useMemo(() => {
    const c = { total: rooms.length };
    for (const s of STATUSES) c[s.key] = 0;
    for (const r of rooms) if (c[r.status] !== undefined) c[r.status] += 1;
    return c;
  }, [rooms]);

  // ── Mutations ────────────────────────────────────────────────────────────
  const openCreate = () => {
    setEditorRoom(EMPTY_ROOM);
    setEditorOpen(true);
  };

  const openEdit = (room) => {
    setEditorRoom({
      ...EMPTY_ROOM,
      ...room,
      amenities: parseAmenities(room.amenities),
      room_type_id: room.room_type_id || "",
    });
    setEditorOpen(true);
    setSelectedRoom(null);
  };

  const saveRoom = async () => {
    const isBulk = !editorRoom.id && editorRoom.bulk;
    // Strip the bulk-only fields before anything hits the API.
    const { bulk, bulk_start, bulk_count, ...rest } = editorRoom;

    if (isBulk) {
      const numbers = generateRoomNumbers(bulk_start, bulk_count);
      if (numbers.length === 0) {
        showMsg("error", "Enter a starting number and how many to create.");
        return;
      }
    } else if (!rest.room_number || !String(rest.room_number).trim()) {
      showMsg("error", "Room number is required.");
      return;
    }

    setSaving(true);
    try {
      const sharedPayload = {
        ...rest,
        room_type_id: rest.room_type_id || null,
        size_sqm: rest.size_sqm === "" ? null : rest.size_sqm,
        amenities: rest.amenities,
      };

      if (isBulk) {
        const room_numbers = generateRoomNumbers(bulk_start, bulk_count);
        const { room_number, ...sharedNoNumber } = sharedPayload;
        const res = await axios.post(roomsBulkUrl, { ...sharedNoNumber, room_numbers });
        const data = res.data || {};
        const createdN = data.created?.length ?? room_numbers.length;
        const skipped  = data.skipped?.length || 0;
        showMsg("success",
          `Created ${createdN} room${createdN === 1 ? "" : "s"}` +
          (skipped ? ` · skipped ${skipped} existing` : "") + ".");
      } else if (editorRoom.id) {
        await axios.put(roomByIdUrl(editorRoom.id), sharedPayload);
        showMsg("success", `Room ${rest.room_number} updated.`);
      } else {
        await axios.post(roomsUrl, sharedPayload);
        showMsg("success", `Room ${rest.room_number} created.`);
      }
      setEditorOpen(false);
      await loadAll();
    } catch (err) {
      console.error("Save room error:", err);
      showMsg("error", err.response?.data?.message || "Could not save room.");
    } finally {
      setSaving(false);
    }
  };

  const deleteRoom = async (id) => {
    try {
      await axios.delete(roomByIdUrl(id));
      showMsg("success", "Room deleted.");
      setConfirmDelete(null);
      setSelectedRoom(null);
      await loadAll();
    } catch (err) {
      console.error("Delete room error:", err);
      showMsg("error", err.response?.data?.message || "Could not delete room.");
    }
  };

  const changeStatus = async (room, status) => {
    try {
      await axios.patch(roomStatusUrl(room.id), { status });
      // Optimistic local update + selectedRoom sync.
      setRooms(rs => rs.map(r => r.id === room.id ? { ...r, status } : r));
      setSelectedRoom(s => s && s.id === room.id ? { ...s, status } : s);
      showMsg("success", `Room ${room.room_number} → ${STATUS_BY_KEY[status].label}.`);
    } catch (err) {
      console.error("Status change error:", err);
      showMsg("error", err.response?.data?.message || "Could not change status.");
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("image", file);
      const res = await axios.post(uploadRoomImageUrl, fd, {
        headers: { "Content-Type":"multipart/form-data" },
      });
      if (res.data?.path) {
        setEditorRoom(r => ({ ...r, image_url: res.data.path }));
      }
    } catch (err) {
      console.error("Image upload error:", err);
      showMsg("error", "Could not upload image.");
    } finally {
      setUploading(false);
    }
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (<>
    {/* Toast */}
    {msg.text && (
      <div style={{
        position:"fixed", top:80, right:32, zIndex:1000,
        padding:"12px 20px", borderRadius:8, fontSize:13, fontWeight:400,
        background: msg.type === "success" ? "#2d6a4f" : "#a93226",
        color:"#fff", boxShadow:"0 8px 24px rgba(40,20,10,.18)",
        animation:"fadeUp .2s both",
      }}>
        {msg.text}
      </div>
    )}

    {/* Stat strip */}
    <div className="g4" style={{ marginBottom:16, gridTemplateColumns:"repeat(7,1fr)" }}>
      <StatCard icon="🏨"  label="Total Rooms" value={counts.total}        active={statusFilter==="all"}        onClick={() => setStatusFilter("all")} />
      {STATUSES.map(s => (
        <StatCard
          key={s.key} icon={STATUS_ICON[s.key]} label={s.label}
          value={counts[s.key]} dotColor={s.dot}
          active={statusFilter === s.key}
          onClick={() => setStatusFilter(statusFilter === s.key ? "all" : s.key)}
        />
      ))}
    </div>

    {/* Toolbar */}
    <div className="panel" style={{ marginBottom:14 }}>
      <div style={{
        display:"flex", flexWrap:"wrap", gap:12, padding:"14px 18px",
        alignItems:"center", justifyContent:"space-between",
      }}>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"center", flex:1, minWidth:0 }}>
          <input
            className="f-input"
            style={{ width:240, padding:"9px 14px", fontSize:13 }}
            placeholder="Search room # / type / view…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          <select className="f-select" style={{ width:160, padding:"9px 14px", fontSize:13 }}
            value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
            <option value="all">All room types</option>
            {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {floors.length > 0 && (
            <select className="f-select" style={{ width:130, padding:"9px 14px", fontSize:13 }}
              value={floorFilter} onChange={e => setFloorFilter(e.target.value)}>
              <option value="all">All floors</option>
              {floors.map(f => <option key={f} value={f}>Floor {f}</option>)}
            </select>
          )}
          <div style={{ display:"flex", gap:4, marginLeft:6 }}>
            <button className={`btn-outline ${view==="grid" ? "active" : ""}`}
                    style={{ padding:"8px 14px", fontSize:11 }}
                    onClick={() => setView("grid")}>Grid</button>
            <button className={`btn-outline ${view==="list" ? "active" : ""}`}
                    style={{ padding:"8px 14px", fontSize:11 }}
                    onClick={() => setView("list")}>List</button>
          </div>
        </div>
        {isAdmin && (
          <div style={{ display:"flex", gap:8 }}>
            <button className="btn-outline" style={{ padding:"9px 16px", fontSize:11 }}
                    onClick={() => setTypeMgrOpen(true)}>
              Room Types ({types.length})
            </button>
            <button className="btn-gold" style={{ padding:"9px 18px", fontSize:11 }}
                    onClick={openCreate}>
              + Add Room
            </button>
          </div>
        )}
      </div>
    </div>

    {/* Body */}
    {loading ? (
      <div className="panel" style={{ padding:60, textAlign:"center", color:"var(--mid)" }}>
        Loading rooms…
      </div>
    ) : loadError ? (
      <div className="panel" style={{ padding:40, textAlign:"center" }}>
        <div style={{ color:"#a93226", fontSize:14, marginBottom:12 }}>{loadError}</div>
        <button className="btn-outline" onClick={loadAll}>Retry</button>
      </div>
    ) : rooms.length === 0 ? (
      <EmptyState isAdmin={isAdmin} onAdd={openCreate} onTypes={() => setTypeMgrOpen(true)} typesCount={types.length} />
    ) : filteredRooms.length === 0 ? (
      <div className="panel" style={{ padding:50, textAlign:"center", color:"var(--mid)" }}>
        No rooms match these filters.
      </div>
    ) : view === "grid" ? (
      <RoomGrid rooms={filteredRooms} onSelect={setSelectedRoom} />
    ) : (
      <RoomList rooms={filteredRooms} onSelect={setSelectedRoom} />
    )}

    {/* Legend */}
    {rooms.length > 0 && (
      <div style={{
        display:"flex", flexWrap:"wrap", gap:18, padding:"14px 6px 0", fontSize:11,
        color:"var(--mid)", fontWeight:300,
      }}>
        {STATUSES.map(s => (
          <div key={s.key} style={{ display:"flex", alignItems:"center", gap:7 }}>
            <span style={{ width:10, height:10, borderRadius:3, background:s.dot, display:"inline-block" }}/>
            {s.label}
          </div>
        ))}
      </div>
    )}

    {/* Detail panel */}
    {selectedRoom && (
      <RoomDetailModal
        room={selectedRoom}
        isAdmin={isAdmin}
        onClose={() => setSelectedRoom(null)}
        onEdit={() => openEdit(selectedRoom)}
        onDelete={() => setConfirmDelete({ id:selectedRoom.id, room_number:selectedRoom.room_number })}
        onStatus={(status) => changeStatus(selectedRoom, status)}
      />
    )}

    {/* Editor */}
    {editorOpen && (
      <RoomEditor
        value={editorRoom}
        onChange={setEditorRoom}
        types={types}
        rooms={rooms}
        saving={saving}
        uploading={uploading}
        onUpload={uploadImage}
        fileInputRef={fileInputRef}
        onClose={() => setEditorOpen(false)}
        onSave={saveRoom}
      />
    )}

    {/* Type manager */}
    {typeMgrOpen && (
      <TypeManager
        types={types}
        onClose={() => { setTypeMgrOpen(false); loadAll(); }}
        showMsg={showMsg}
      />
    )}

    {/* Delete confirm */}
    {confirmDelete && (
      <ConfirmDialog
        title={`Delete room ${confirmDelete.room_number}?`}
        body="This permanently removes the room record. Any bookings linked to this room will lose their reference."
        confirmLabel="Delete room"
        onCancel={() => setConfirmDelete(null)}
        onConfirm={() => deleteRoom(confirmDelete.id)}
      />
    )}
  </>);
}

// ── Small icon map (kept beside its only use to avoid noise above) ─────────
const STATUS_ICON = {
  available:    "🟢",
  occupied:     "🟡",
  reserved:     "🔵",
  cleaning:     "🧹",
  maintenance:  "🛠",
  out_of_order: "🔴",
};

// ═══════════════════════════════════════════════════════════════════════════
//  PRESENTATIONAL CHILDREN
// ═══════════════════════════════════════════════════════════════════════════

function StatCard({ icon, label, value, dotColor, active, onClick }) {
  return (
    <button onClick={onClick} className="stat"
      style={{
        textAlign:"left", border: active ? "1.5px solid var(--gold)" : "1px solid var(--border)",
        background: active ? "rgba(200,160,90,.06)" : "var(--white)",
        cursor:"pointer", fontFamily:"inherit", padding:18,
      }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
        <div style={{ fontSize:20 }}>{icon}</div>
        {dotColor && <div style={{ width:9, height:9, borderRadius:"50%", background:dotColor }} />}
      </div>
      <div className="stat-val" style={{ fontSize:28 }}>{value}</div>
      <div className="stat-lbl" style={{ marginBottom:0 }}>{label}</div>
    </button>
  );
}

function RoomGrid({ rooms, onSelect }) {
  return (
    <div className="panel">
      <div style={{
        display:"grid", gap:12, padding:18,
        gridTemplateColumns:"repeat(auto-fill,minmax(180px,1fr))",
      }}>
        {rooms.map(r => <RoomTile key={r.id} room={r} onClick={() => onSelect(r)} />)}
      </div>
    </div>
  );
}

function RoomTile({ room, onClick }) {
  const s = STATUS_BY_KEY[room.status] || STATUS_BY_KEY.available;
  const amenities = parseAmenities(room.amenities);
  const hasImage  = !!room.image_url;

  // When the room has a photo we use it as the tile background, dimmed by
  // a dark gradient so every text element stays legible.  Without a photo
  // we fall back to the original status-tinted block exactly as before.
  const tileStyle = hasImage
    ? {
        backgroundImage:
          `linear-gradient(180deg, rgba(28,16,8,.55) 0%, rgba(20,10,5,.78) 100%), url("${ASSETS}${room.image_url}")`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        color: "#f0e4cf",
        border: "1px solid rgba(200,160,90,.28)",
        borderRadius: 10,
        padding: "14px 14px 12px",
        position: "relative",
        cursor: "pointer",
        transition: "transform .15s, box-shadow .15s, filter .15s",
        boxShadow: "0 2px 10px rgba(40,20,10,.08)",
        overflow: "hidden",
      }
    : {
        background: s.bg, color: s.color, border: s.border,
        borderRadius: 10, padding: "14px 14px 12px", position: "relative",
        cursor: "pointer", transition: "transform .15s, box-shadow .15s",
      };

  return (
    <div onClick={onClick}
      style={tileStyle}
      onMouseEnter={e => {
        e.currentTarget.style.transform = "translateY(-2px)";
        e.currentTarget.style.boxShadow = "0 6px 18px rgba(40,20,10,.18)";
        if (hasImage) e.currentTarget.style.filter = "brightness(1.05)";
      }}
      onMouseLeave={e => {
        e.currentTarget.style.transform = "";
        e.currentTarget.style.boxShadow = hasImage ? "0 2px 10px rgba(40,20,10,.08)" : "";
        if (hasImage) e.currentTarget.style.filter = "";
      }}>
      <div style={{
        position: "absolute", top: 12, right: 12,
        width: 10, height: 10, borderRadius: "50%", background: s.dot,
        boxShadow: hasImage ? "0 0 0 2px rgba(0,0,0,.35)" : "none",
      }}/>
      <div style={{
        fontFamily: "'Cormorant Garamond',serif", fontSize: 24, lineHeight: 1,
        marginBottom: 3,
        textShadow: hasImage ? "0 1px 6px rgba(0,0,0,.55)" : "none",
      }}>
        {room.room_number}
      </div>
      <div style={{
        fontSize: 9.5, letterSpacing: ".14em", textTransform: "uppercase",
        opacity: hasImage ? .85 : .7, marginBottom: 8,
        textShadow: hasImage ? "0 1px 4px rgba(0,0,0,.5)" : "none",
      }}>
        {room.room_type_name || "Untyped"}
        {room.floor ? ` · Floor ${room.floor}` : ""}
      </div>
      <div style={{
        fontSize: 13, fontWeight: 500, marginBottom: 4,
        textShadow: hasImage ? "0 1px 4px rgba(0,0,0,.5)" : "none",
      }}>
        GH₵ {Number(room.price_per_night || 0).toFixed(0)}
        <span style={{ fontSize: 10, opacity: .7 }}>/night</span>
      </div>
      <div style={{
        display: "flex", gap: 10, fontSize: 10.5,
        opacity: hasImage ? .95 : .8,
        textShadow: hasImage ? "0 1px 3px rgba(0,0,0,.5)" : "none",
      }}>
        {room.max_occupancy ? <span>👥 {room.max_occupancy}</span> : null}
        {room.bed_type      ? <span>🛏 {room.bed_type}</span> : null}
        {room.view_type     ? <span>🪟 {room.view_type}</span> : null}
      </div>
      {amenities.length > 0 && (
        <div style={{ marginTop: 8, display: "flex", gap: 4, flexWrap: "wrap" }}>
          {amenities.slice(0, 4).map(k => (
            <span key={k} title={AMENITY_BY_KEY[k]?.label || k}
                  style={{
                    fontSize: 11,
                    opacity: hasImage ? .95 : .85,
                    textShadow: hasImage ? "0 1px 3px rgba(0,0,0,.6)" : "none",
                  }}>
              {AMENITY_BY_KEY[k]?.icon || "•"}
            </span>
          ))}
          {amenities.length > 4 && (
            <span style={{
              fontSize: 10, opacity: .7,
              textShadow: hasImage ? "0 1px 3px rgba(0,0,0,.6)" : "none",
            }}>+{amenities.length - 4}</span>
          )}
        </div>
      )}
      <div style={{
        marginTop: 10, paddingTop: 8,
        borderTop: `1px dashed ${hasImage ? "rgba(240,228,207,.35)" : "currentColor"}`,
        opacity: hasImage ? .8 : .6,
        fontSize: 10, letterSpacing: ".1em", textTransform: "uppercase",
        textShadow: hasImage ? "0 1px 3px rgba(0,0,0,.5)" : "none",
      }}>
        {s.label}
      </div>
    </div>
  );
}

function RoomList({ rooms, onSelect }) {
  return (
    <div className="panel">
      <div style={{ overflowX:"auto" }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>Room</th><th>Type</th><th>Floor</th><th>Bed</th>
              <th>Occupancy</th><th>Price / night</th><th>View</th><th>Status</th><th></th>
            </tr>
          </thead>
          <tbody>
            {rooms.map(r => {
              const s = STATUS_BY_KEY[r.status] || STATUS_BY_KEY.available;
              return (
                <tr key={r.id} style={{ cursor:"pointer" }} onClick={() => onSelect(r)}>
                  <td style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18 }}>{r.room_number}</td>
                  <td>{r.room_type_name || "—"}</td>
                  <td>{r.floor || "—"}</td>
                  <td>{r.bed_type || "—"}</td>
                  <td>{r.max_occupancy || "—"}</td>
                  <td>GH₵ {Number(r.price_per_night || 0).toFixed(2)}</td>
                  <td>{r.view_type || "—"}</td>
                  <td>
                    <span className="badge" style={{ background:s.bg, color:s.color, border:s.border }}>
                      {s.label}
                    </span>
                  </td>
                  <td style={{ textAlign:"right", color:"var(--mid)" }}>›</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function EmptyState({ isAdmin, onAdd, onTypes, typesCount }) {
  return (
    <div className="panel" style={{ padding:"60px 30px", textAlign:"center" }}>
      <div style={{ fontSize:48, marginBottom:14, opacity:.6 }}>🏨</div>
      <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:"var(--dark)", marginBottom:8 }}>
        No rooms yet
      </div>
      <div style={{ fontSize:13, color:"var(--mid)", maxWidth:420, margin:"0 auto 20px", lineHeight:1.6 }}>
        Add your room types first (Standard, Deluxe, Suite, …), then register each physical room against a type. Each property defines its own categories — nothing is hardcoded.
      </div>
      {isAdmin ? (
        <div style={{ display:"flex", gap:10, justifyContent:"center" }}>
          {typesCount === 0 && (
            <button className="btn-dark" onClick={onTypes}>Set up room types</button>
          )}
          <button className="btn-gold" onClick={onAdd} disabled={typesCount===0}>
            + Add first room
          </button>
        </div>
      ) : (
        <div style={{ fontSize:12, color:"var(--mid)" }}>
          Ask a super admin to add rooms.
        </div>
      )}
    </div>
  );
}

// ── Detail panel modal ─────────────────────────────────────────────────────
function RoomDetailModal({ room, isAdmin, onClose, onEdit, onDelete, onStatus }) {
  const s = STATUS_BY_KEY[room.status] || STATUS_BY_KEY.available;
  const amenities = parseAmenities(room.amenities);
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width:"min(640px,94vw)" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:24, color:"#f0e4cf" }}>
              Room {room.room_number}
            </div>
            <div style={{ fontSize:11, letterSpacing:".15em", textTransform:"uppercase", color:"rgba(200,160,90,.55)", marginTop:2 }}>
              {room.room_type_name || "Untyped"}{room.floor ? ` · Floor ${room.floor}` : ""}
            </div>
          </div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ padding:0 }}>
          {room.image_url && (
            <img src={`${ASSETS}${room.image_url}`} alt=""
                 style={{ width:"100%", height:200, objectFit:"cover", display:"block" }} />
          )}
          <div style={{ padding:24 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
              <span className="badge" style={{ background:s.bg, color:s.color, border:s.border, fontSize:11 }}>
                ● {s.label}
              </span>
              <span style={{ fontSize:18, color:"var(--dark)", fontWeight:500 }}>
                GH₵ {Number(room.price_per_night || 0).toFixed(2)}
                <span style={{ fontSize:11, color:"var(--mid)", marginLeft:4 }}>/ night</span>
              </span>
            </div>

            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:14, marginBottom:18 }}>
              <FactCell label="Bed type"  value={room.bed_type || "—"} />
              <FactCell label="Occupancy" value={room.max_occupancy ? `${room.max_occupancy} guests` : "—"} />
              <FactCell label="View"      value={room.view_type || "—"} />
              <FactCell label="Size"      value={room.size_sqm ? `${room.size_sqm} m²` : "—"} />
              <FactCell label="Floor"     value={room.floor || "—"} />
              <FactCell label="Active"    value={room.is_active ? "Yes" : "No"} />
            </div>

            {room.description && (
              <div style={{ marginBottom:16 }}>
                <div className="f-label">Description</div>
                <div style={{ fontSize:13, color:"#4a3828", fontWeight:300, lineHeight:1.6 }}>
                  {room.description}
                </div>
              </div>
            )}

            {amenities.length > 0 && (
              <div style={{ marginBottom:18 }}>
                <div className="f-label">Amenities</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
                  {amenities.map(k => (
                    <span key={k} className="badge badge-gold" style={{ fontSize:11, textTransform:"none", letterSpacing:".03em" }}>
                      {AMENITY_BY_KEY[k]?.icon || "•"} {AMENITY_BY_KEY[k]?.label || k}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {room.notes && (
              <div style={{ marginBottom:18, padding:12, background:"rgba(200,160,90,.05)", borderRadius:7, border:"1px dashed rgba(200,160,90,.25)" }}>
                <div className="f-label" style={{ marginBottom:4 }}>Internal notes</div>
                <div style={{ fontSize:12.5, color:"#5a4838", fontWeight:300, lineHeight:1.55 }}>
                  {room.notes}
                </div>
              </div>
            )}

            <div className="f-label">Change status</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:18 }}>
              {STATUSES.map(opt => (
                <button key={opt.key}
                  className="btn-outline"
                  style={{
                    padding:"7px 12px", fontSize:10.5, letterSpacing:".08em",
                    borderColor: room.status === opt.key ? opt.dot : "var(--border)",
                    color:       room.status === opt.key ? opt.dot : "var(--mid)",
                    background:  room.status === opt.key ? opt.bg : "transparent",
                  }}
                  disabled={room.status === opt.key}
                  onClick={() => onStatus(opt.key)}>
                  {STATUS_ICON[opt.key]} {opt.label}
                </button>
              ))}
            </div>

            {isAdmin && (
              <div style={{ display:"flex", gap:8, justifyContent:"flex-end", paddingTop:14, borderTop:"1px solid var(--border)" }}>
                <button className="btn-outline" style={{ color:"#a93226", borderColor:"rgba(192,57,43,.3)" }}
                        onClick={onDelete}>
                  Delete
                </button>
                <button className="btn-gold" onClick={onEdit}>Edit room</button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function FactCell({ label, value }) {
  return (
    <div>
      <div className="f-label" style={{ marginBottom:3 }}>{label}</div>
      <div style={{ fontSize:14, color:"var(--dark)", fontWeight:400 }}>{value}</div>
    </div>
  );
}

// ── Inherited rates panel ────────────────────────────────────────────────
// Shows the nightly / hourly / daily rates of the selected room type so
// the operator never has to re-enter them when registering a room.  A
// secondary "Override nightly rate for this room" expander surfaces an
// input for the rare per-unit adjustment (e.g. a premium room within a
// Standard type).  Spans both columns of the form grid.
function InheritedRatesPanel({ type, override, onOverride }) {
  const [editing, setEditing] = React.useState(false);

  // No type chosen yet — show a gentle prompt rather than a busy panel.
  if (!type) {
    return (
      <div style={{
        gridColumn: "1 / -1",
        padding: "12px 14px", borderRadius: 8,
        border: "1px dashed rgba(180,140,80,.35)",
        background: "rgba(200,160,90,.04)",
        fontSize: 12.5, color: "var(--mid)",
      }}>
        Pick a <strong style={{ color: "var(--dark)" }}>room type</strong> above — its nightly, hourly, and daily rates will be applied automatically.
      </div>
    );
  }

  const baseN = Number(type.base_price)  || 0;
  const baseH = Number(type.hourly_rate) || 0;
  const baseD = Number(type.daily_rate)  || 0;
  // The override is active when it's set AND differs from the type's
  // base price (otherwise it's just mirroring the type).
  const overrideNum = Number(override);
  const hasOverride = override !== "" && override !== null && override !== undefined
                      && overrideNum > 0 && overrideNum !== baseN;
  const effectiveNightly = hasOverride ? overrideNum : baseN;

  const chipStyle = (active) => ({
    flex: 1, minWidth: 110,
    padding: "10px 12px", borderRadius: 8,
    background: active
      ? "linear-gradient(135deg, rgba(200,160,90,.18), rgba(200,160,90,.06))"
      : "rgba(253,248,242,.7)",
    border: `1px solid ${active ? "rgba(200,160,90,.4)" : "rgba(180,140,80,.18)"}`,
  });

  return (
    <div style={{
      gridColumn: "1 / -1",
      padding: "12px 14px", borderRadius: 8,
      background: "#fdf8f2",
      border: "1px solid rgba(180,140,80,.22)",
    }}>
      <div style={{
        display: "flex", justifyContent: "space-between", alignItems: "baseline",
        marginBottom: 10,
      }}>
        <div style={{ fontSize: 11, letterSpacing: ".14em", textTransform: "uppercase", color: "#6b5142", fontWeight: 500 }}>
          Rates from {type.name}
        </div>
        <button
          type="button"
          onClick={() => setEditing(e => !e)}
          style={{
            background: "transparent", border: "none",
            color: "var(--gold)", cursor: "pointer",
            fontSize: 11, letterSpacing: ".06em", fontFamily: "'Jost', sans-serif",
            padding: 0,
          }}>
          {editing ? "✕ Close override" : (hasOverride ? "✎ Edit override" : "✎ Override for this room")}
        </button>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        <div style={chipStyle(true)}>
          <div style={{ fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "#8a6a30" }}>Nightly</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: "var(--dark)", marginTop: 2 }}>
            GH₵ {effectiveNightly.toFixed(2)}
            {hasOverride && (
              <span style={{ fontFamily: "'Jost', sans-serif", fontSize: 10.5, color: "#a93226", marginLeft: 6 }}>override</span>
            )}
          </div>
          {hasOverride && (
            <div style={{ fontSize: 10.5, color: "var(--mid)", marginTop: 2, textDecoration: "line-through" }}>
              Type default: GH₵ {baseN.toFixed(2)}
            </div>
          )}
        </div>
        <div style={chipStyle(baseH > 0)}>
          <div style={{ fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "#8a6a30" }}>Hourly</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: baseH > 0 ? "var(--dark)" : "var(--mid)", marginTop: 2 }}>
            {baseH > 0 ? `GH₵ ${baseH.toFixed(2)}` : "—"}
          </div>
        </div>
        <div style={chipStyle(baseD > 0)}>
          <div style={{ fontSize: 10, letterSpacing: ".12em", textTransform: "uppercase", color: "#8a6a30" }}>Daily</div>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 20, color: baseD > 0 ? "var(--dark)" : "var(--mid)", marginTop: 2 }}>
            {baseD > 0 ? `GH₵ ${baseD.toFixed(2)}` : "—"}
          </div>
        </div>
      </div>

      {/* Override editor — collapsed by default.  Only the nightly rate
          can be overridden per room; hourly + daily live on the type. */}
      {editing && (
        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px dashed rgba(180,140,80,.3)" }}>
          <div className="f-label" style={{ marginBottom: 6 }}>Override nightly rate for this room (GH₵)</div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              className="f-input"
              type="number" min="0" step="0.01"
              value={override ?? ""}
              placeholder={`Type default: ${baseN.toFixed(2)}`}
              onChange={(e) => onOverride(e.target.value)}
              style={{ flex: 1 }}
            />
            {hasOverride && (
              <button type="button" className="btn-outline"
                style={{ padding: "9px 14px", fontSize: 11 }}
                onClick={() => onOverride(baseN)}>
                Reset
              </button>
            )}
          </div>
          <div style={{ fontSize: 11.5, color: "var(--mid)", marginTop: 6 }}>
            Hourly and daily rates always follow the room type — change them in <strong style={{ color: "var(--dark)" }}>Room Types</strong> to apply to every room of this type.
          </div>
        </div>
      )}
    </div>
  );
}

// ── Editor modal ───────────────────────────────────────────────────────────
function RoomEditor({ value, onChange, types, rooms, saving, uploading, onUpload, fileInputRef, onClose, onSave }) {
  const set = (k, v) => onChange({ ...value, [k]: v });
  const toggleAmenity = (key) => {
    const has = value.amenities.includes(key);
    set("amenities", has ? value.amenities.filter(a => a !== key) : [...value.amenities, key]);
  };

  // Pre-flight duplicate check so the user sees the collision while typing,
  // not after they hit Save.  Server still rejects duplicates as the final
  // authority (UNIQUE constraint + explicit check) — this is purely UX.
  const existingNumbers = (rooms || []).reduce((m, r) => {
    if (!value.id || r.id !== value.id) m.set(String(r.room_number).trim().toLowerCase(), r);
    return m;
  }, new Map());

  const trimmedNumber = String(value.room_number || "").trim();
  const singleDuplicate = !value.bulk && trimmedNumber
    ? existingNumbers.get(trimmedNumber.toLowerCase()) || null
    : null;

  const bulkCollisions = value.bulk && !value.id
    ? generateRoomNumbers(value.bulk_start, value.bulk_count)
        .filter(n => existingNumbers.has(String(n).trim().toLowerCase()))
    : [];

  // Block save only on the single-room duplicate case.  Bulk creates are
  // already allowed to skip-and-continue server-side (POST /rooms/bulk
  // returns `skipped`), so we just warn and let the user proceed.
  const blockSave = !!singleDuplicate;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-box" style={{ width:"min(720px,96vw)", maxHeight:"92vh", display:"flex", flexDirection:"column" }}
           onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{value.id ? `Edit Room ${value.room_number}` : "Add a Room"}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body" style={{ overflowY:"auto", flex:1 }}>

          {/* Image */}
          <div className="f-group">
            <div className="f-label">Room photo (optional)</div>
            <div style={{
              border:"1.5px dashed #d8c4ad", borderRadius:8, padding:14,
              display:"flex", alignItems:"center", gap:14, background:"#fdf8f2",
            }}>
              {value.image_url ? (
                <img src={`${ASSETS}${value.image_url}`} alt=""
                     style={{ width:90, height:68, objectFit:"cover", borderRadius:6 }} />
              ) : (
                <div style={{
                  width:90, height:68, borderRadius:6, background:"#f0e4cf",
                  display:"flex", alignItems:"center", justifyContent:"center", fontSize:24, color:"var(--gold)",
                }}>📷</div>
              )}
              <div style={{ flex:1 }}>
                <input ref={fileInputRef} type="file" accept="image/*" style={{ display:"none" }}
                       onChange={e => onUpload(e.target.files?.[0])} />
                <button type="button" className="btn-outline"
                        style={{ padding:"7px 14px", fontSize:11 }}
                        disabled={uploading}
                        onClick={() => fileInputRef.current?.click()}>
                  {uploading ? "Uploading…" : value.image_url ? "Replace photo" : "Upload photo"}
                </button>
                {value.image_url && (
                  <button type="button" className="btn-outline"
                          style={{ padding:"7px 14px", fontSize:11, marginLeft:6 }}
                          onClick={() => set("image_url", "")}>
                    Remove
                  </button>
                )}
                <div style={{ fontSize:11, color:"var(--mid)", marginTop:6 }}>
                  JPG / PNG up to 8 MB.
                </div>
              </div>
            </div>
          </div>

          {/* Bulk-create toggle — only on new rooms.  When editing, we always
              operate on a single record. */}
          {!value.id && (
            <div style={{
              marginBottom:16, padding:"12px 14px",
              background: value.bulk ? "rgba(200,160,90,.07)" : "#fdf8f2",
              border:"1px solid var(--border)", borderRadius:8,
            }}>
              <label style={{ display:"flex", alignItems:"center", gap:10, cursor:"pointer" }}>
                <input type="checkbox" checked={value.bulk}
                       onChange={e => set("bulk", e.target.checked)} />
                <div>
                  <div style={{ fontSize:13, color:"var(--dark)", fontWeight:400 }}>
                    Add multiple rooms at once
                  </div>
                  <div style={{ fontSize:11, color:"var(--mid)", marginTop:2 }}>
                    Same type, price, amenities — different room numbers.
                  </div>
                </div>
              </label>
            </div>
          )}

          {value.bulk && !value.id && (() => {
            const preview = generateRoomNumbers(value.bulk_start, value.bulk_count);
            if (preview.length === 0) return null;
            const head = preview.slice(0, 3);
            const tail = preview.length > 4 ? preview[preview.length - 1] : null;
            return (
              <>
                <div style={{
                  marginBottom: bulkCollisions.length ? 8 : 16, padding:"10px 14px",
                  background:"#fff", border:"1px solid var(--border)", borderRadius:7,
                  fontSize:12, color:"var(--mid)",
                }}>
                  <span style={{ fontSize:11, letterSpacing:".12em", textTransform:"uppercase", color:"#6b5a4e", marginRight:8 }}>
                    Will create {preview.length - bulkCollisions.length} of {preview.length}:
                  </span>
                  <span style={{ color:"var(--dark)", fontWeight:400 }}>
                    {head.join(", ")}
                    {preview.length > 3 ? (tail ? `, … , ${tail}` : `, ${preview[3]}`) : ""}
                  </span>
                </div>
                {bulkCollisions.length > 0 && (
                  <div style={{
                    marginBottom: 16, padding: "10px 14px", borderRadius: 7,
                    background: "#fef0ee", border: "1px solid #f5c5bc",
                    color: "#a93226", fontSize: 12.5,
                  }}>
                    ⚠️ {bulkCollisions.length} number{bulkCollisions.length === 1 ? "" : "s"} in this range
                    already exist and will be skipped:&nbsp;
                    <span style={{ fontWeight: 500 }}>
                      {bulkCollisions.slice(0, 5).join(", ")}
                      {bulkCollisions.length > 5 ? `, … (+${bulkCollisions.length - 5} more)` : ""}
                    </span>
                  </div>
                )}
              </>
            );
          })()}

          <div className="form-grid">
            {value.bulk && !value.id ? (
              <>
                <Field label="Starting number *">
                  <input className="f-input" value={value.bulk_start}
                         onChange={e => set("bulk_start", e.target.value)}
                         placeholder="e.g. 201 · G05 · VVIP-1" />
                </Field>
                <Field label="How many? *">
                  <input type="number" min="1" max="500" className="f-input" value={value.bulk_count}
                         onChange={e => set("bulk_count", e.target.value)} />
                </Field>
              </>
            ) : (
              <Field label="Room number *">
                <input
                  className="f-input"
                  value={value.room_number}
                  onChange={e => set("room_number", e.target.value)}
                  placeholder="e.g. 101 or G-12"
                  style={singleDuplicate
                    ? { borderColor: "#c0392b", boxShadow: "0 0 0 3px rgba(192,57,43,.12)" }
                    : undefined}
                  aria-invalid={!!singleDuplicate}
                />
                {singleDuplicate && (
                  <div style={{
                    marginTop: 6, fontSize: 12, color: "#a93226", fontWeight: 500,
                    display: "flex", alignItems: "center", gap: 6,
                  }}>
                    ⚠️ Room "{singleDuplicate.room_number}" already exists
                    {singleDuplicate.room_type_name ? ` (${singleDuplicate.room_type_name})` : ""}.
                    {value.id
                      ? " Choose a different number."
                      : " Pick a different number, or edit that room from the list."}
                  </div>
                )}
              </Field>
            )}
            <Field label="Room type">
              <select className="f-select" value={value.room_type_id || ""}
                      onChange={e => {
                        // Cascade pricing + occupancy from the chosen type
                        // so the operator doesn't have to re-enter them.
                        // We only overwrite a field if it's still empty or
                        // it matched the *previous* type's value — that way
                        // an explicit per-room override (someone typed a
                        // custom price by hand) survives a type swap.
                        const newId = e.target.value;
                        const newType = types.find(t => String(t.id) === newId);
                        const oldType = types.find(t => String(t.id) === String(value.room_type_id));
                        const next    = { ...value, room_type_id: newId };
                        if (newType) {
                          const curPrice = String(value.price_per_night ?? "");
                          const oldPrice = oldType ? String(Number(oldType.base_price)) : "";
                          if (curPrice === "" || curPrice === "0" || curPrice === oldPrice) {
                            next.price_per_night = newType.base_price;
                          }
                          const curOcc = String(value.max_occupancy ?? "");
                          const oldOcc = oldType ? String(Number(oldType.base_occupancy)) : "";
                          if (curOcc === "" || curOcc === oldOcc) {
                            next.max_occupancy = newType.base_occupancy;
                          }
                        }
                        onChange(next);
                      }}>
                <option value="">— pick a type —</option>
                {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </Field>

            {/* Inherited rates from the selected type.  Replaces the old
                "Price per night" input — the room reuses whatever its
                type charges, and a per-room override is hidden behind a
                small toggle so the common case stays one click. */}
            <InheritedRatesPanel
              type={types.find(t => String(t.id) === String(value.room_type_id))}
              override={value.price_per_night}
              onOverride={(v) => set("price_per_night", v)}
            />

            <Field label="Floor">
              <input className="f-input" value={value.floor || ""}
                     onChange={e => set("floor", e.target.value)}
                     placeholder="e.g. 1, G, M" />
            </Field>
            <Field label="Max occupancy">
              <input type="number" min="1" className="f-input" value={value.max_occupancy}
                     onChange={e => set("max_occupancy", e.target.value)} />
            </Field>
            <Field label="Size (m²)">
              <input type="number" min="0" step="0.1" className="f-input" value={value.size_sqm || ""}
                     onChange={e => set("size_sqm", e.target.value)} />
            </Field>
            <Field label="Bed type">
              <select className="f-select" value={value.bed_type || ""}
                      onChange={e => set("bed_type", e.target.value)}>
                <option value="">— select —</option>
                {BED_TYPES.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
            </Field>
            <Field label="View">
              <select className="f-select" value={value.view_type || ""}
                      onChange={e => set("view_type", e.target.value)}>
                <option value="">— select —</option>
                {VIEW_TYPES.map(v => <option key={v} value={v}>{v}</option>)}
              </select>
            </Field>
          </div>

          <div className="f-group">
            <div className="f-label">Initial status</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {STATUSES.map(s => (
                <button key={s.key} type="button"
                  className="btn-outline"
                  style={{
                    padding:"7px 12px", fontSize:10.5,
                    borderColor: value.status === s.key ? s.dot : "var(--border)",
                    color:       value.status === s.key ? s.dot : "var(--mid)",
                    background:  value.status === s.key ? s.bg  : "transparent",
                  }}
                  onClick={() => set("status", s.key)}>
                  {STATUS_ICON[s.key]} {s.label}
                </button>
              ))}
            </div>
          </div>

          <div className="f-group">
            <div className="f-label">Amenities</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:6 }}>
              {AMENITY_OPTIONS.map(a => {
                const on = value.amenities.includes(a.key);
                return (
                  <button key={a.key} type="button"
                    onClick={() => toggleAmenity(a.key)}
                    className="btn-outline"
                    style={{
                      padding:"7px 11px", fontSize:11, textTransform:"none", letterSpacing:".02em",
                      borderColor: on ? "var(--gold)" : "var(--border)",
                      color:       on ? "var(--dark)" : "var(--mid)",
                      background:  on ? "rgba(200,160,90,.12)" : "transparent",
                    }}>
                    {a.icon} {a.label}
                  </button>
                );
              })}
            </div>
          </div>

          <div className="f-group">
            <div className="f-label">Description (guest-facing)</div>
            <textarea className="f-input" rows={3} value={value.description || ""}
                      onChange={e => set("description", e.target.value)}
                      placeholder="A short, descriptive paragraph that can be reused in booking pages." />
          </div>

          <div className="f-group">
            <div className="f-label">Internal notes (staff only)</div>
            <textarea className="f-input" rows={2} value={value.notes || ""}
                      onChange={e => set("notes", e.target.value)}
                      placeholder="e.g. AC unit makes noise on the high setting; replace this quarter." />
          </div>
        </div>

        <div style={{ padding:"14px 26px", borderTop:"1px solid var(--border)", display:"flex", justifyContent:"flex-end", gap:8 }}>
          <button className="btn-outline" onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className="btn-gold"
            onClick={onSave}
            disabled={saving || blockSave}
            title={blockSave ? `Room "${singleDuplicate.room_number}" already exists` : undefined}
          >
            {saving
              ? "Saving…"
              : blockSave
                ? "Room number taken"
                : value.id
                  ? "Save changes"
                  : value.bulk
                    ? `Create ${generateRoomNumbers(value.bulk_start, value.bulk_count).length || ""} room${generateRoomNumbers(value.bulk_start, value.bulk_count).length === 1 ? "" : "s"}`.trim()
                    : "Create room"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="f-group">
      <div className="f-label">{label}</div>
      {children}
    </div>
  );
}

// ── Room type manager ──────────────────────────────────────────────────────
const EMPTY_GEN = { start: "", count: 10, floor: "" };

function TypeManager({ types: initialTypes, onClose, showMsg }) {
  const [types,    setTypes]    = useState(initialTypes);
  const [form,     setForm]     = useState(EMPTY_TYPE);
  const [editing,  setEditing]  = useState(null);
  const [busy,     setBusy]     = useState(false);
  // When editing: force the new nightly price / occupancy onto every room
  // of the type, even ones priced individually.
  const [applyAll, setApplyAll] = useState(true);
  // ── Per-row "generate rooms" expander.  `genFor` is the id of the type
  //    whose generator is currently open, or null.
  const [genFor,   setGenFor]   = useState(null);
  const [gen,      setGen]      = useState(EMPTY_GEN);
  const [genBusy,  setGenBusy]  = useState(false);

  // Re-pull from server after every mutation so counts stay accurate.
  const reload = async () => {
    try {
      const res = await axios.get(roomTypesUrl);
      setTypes(res.data?.results || []);
    } catch (err) {
      console.error("Type reload error:", err);
    }
  };

  // A type is useless without at least one rate, since reservations need
  // *something* to multiply by their nights/hours/days.  Require at least
  // one of the three before allowing the save.
  const ratesEntered = () => {
    const n = Number(form.base_price);
    const h = Number(form.hourly_rate);
    const d = Number(form.daily_rate);
    return (n > 0) || (h > 0) || (d > 0);
  };

  const save = async () => {
    if (!form.name.trim()) {
      showMsg("error", "Type name is required.");
      return;
    }
    if (!ratesEntered()) {
      showMsg("error", "Enter at least one rate — nightly, hourly, or daily.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || null,
        base_price: form.base_price === "" ? 0 : Number(form.base_price),
        hourly_rate: form.hourly_rate === "" ? null : Number(form.hourly_rate),
        daily_rate:  form.daily_rate  === "" ? null : Number(form.daily_rate),
        base_occupancy: form.base_occupancy === "" ? 2 : Number(form.base_occupancy),
      };
      if (editing) {
        const res = await axios.put(roomTypeByIdUrl(editing), { ...payload, apply_to_all_rooms: applyAll });
        const n = res.data?.rooms_repriced || 0;
        showMsg("success", "Room type updated" +
          (n ? ` · nightly price updated on ${n} room${n === 1 ? "" : "s"}` : "") + ".");
      } else {
        await axios.post(roomTypesUrl, payload);
        showMsg("success", "Room type created.");
      }
      setForm(EMPTY_TYPE); setEditing(null); setApplyAll(true);
      await reload();
    } catch (err) {
      console.error("Type save error:", err);
      showMsg("error", err.response?.data?.message || "Could not save room type.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete room type "${t.name}"?`)) return;
    try {
      await axios.delete(roomTypeByIdUrl(t.id));
      showMsg("success", "Room type deleted.");
      await reload();
    } catch (err) {
      console.error("Type delete error:", err);
      showMsg("error", err.response?.data?.message || "Could not delete room type.");
    }
  };

  const openGen = (t) => {
    setGenFor(t.id);
    setGen(EMPTY_GEN);
  };

  const submitGen = async (t) => {
    const numbers = generateRoomNumbers(gen.start, gen.count);
    if (numbers.length === 0) {
      showMsg("error", "Enter a starting number and how many to create.");
      return;
    }
    setGenBusy(true);
    try {
      // The type's base_price + base_occupancy are the defaults each new room
      // inherits.  The admin can still edit individual rooms later for one-off
      // variations (e.g. a sea-view premium on a single VVIP unit).
      const res = await axios.post(roomsBulkUrl, {
        room_numbers: numbers,
        room_type_id: t.id,
        floor: gen.floor || null,
        price_per_night: t.base_price,
        max_occupancy: t.base_occupancy,
        status: "available",
      });
      const data = res.data || {};
      const created = data.created?.length ?? numbers.length;
      const skipped = data.skipped?.length || 0;
      showMsg("success",
        `Created ${created} ${t.name} room${created === 1 ? "" : "s"}` +
        (skipped ? ` · skipped ${skipped} existing` : "") + ".");
      setGenFor(null);
      setGen(EMPTY_GEN);
      await reload();
    } catch (err) {
      console.error("Generate rooms error:", err);
      showMsg("error", err.response?.data?.message || "Could not create rooms.");
    } finally {
      setGenBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <style>{`
        .rt-split { flex:1; min-height:0; display:flex; flex-direction:row; }
        .rt-list  { flex:1.4 1 0; min-width:0; min-height:0; overflow-y:auto; padding:14px 20px 18px; }
        .rt-form  { flex:1 1 0; min-width:300px; min-height:0; overflow-y:auto;
                    border-left:1px solid var(--border); background:rgba(200,160,90,.04);
                    padding:16px 20px 18px; }
        .rt-form .form-grid { grid-template-columns:1fr 1fr; }
        .rt-card-row { display:flex; justify-content:space-between; align-items:center;
                       flex-wrap:wrap; gap:10px; padding:12px 14px; }
        .rt-actions { display:flex; gap:6px; flex-wrap:wrap; }
        @media (max-width: 1000px) {
          .rt-form .form-grid { grid-template-columns:1fr; }
        }
        /* Phones: not enough width for two columns — stack, and let the
           whole body scroll as one so neither pane gets squeezed. */
        @media (max-width: 700px) {
          .rt-split { flex-direction:column; overflow-y:auto; }
          .rt-list, .rt-form { flex:none; overflow:visible; min-width:0; }
          .rt-form { border-left:none; border-top:1px solid var(--border); }
          .rt-list { padding:12px 14px 14px; }
          .rt-form { padding:14px 14px 16px; }
        }
      `}</style>
      <div className="modal-box" style={{ width:"min(1180px,96vw)", height:"92vh", maxHeight:"92vh", display:"flex", flexDirection:"column" }}
           onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">Room Types</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        {/* Split body: existing types on the left and the "Add / Edit
            room type" form on the right, each scrolling independently.
            On phones the two panes stack (see .rt-split media query). */}
        <div className="modal-body" style={{
          flex: 1, minHeight: 0, padding: 0,
          display: "flex", flexDirection: "column",
        }}>
          <div style={{
            padding: "14px 24px 12px",
            borderBottom: "1px solid var(--border)",
            flexShrink: 0,
            fontSize: 12, color: "var(--mid)", lineHeight: 1.5,
          }}>
            Define the categories your facility offers. Each room must reference a type, which controls its default price and occupancy. Use <strong style={{ color:"var(--dark)" }}>+ Generate rooms</strong> on a type to register many at once.
          </div>

          <div className="rt-split">
          {/* Left pane — existing types, scrolls inside itself. */}
          <div className="rt-list">
          {types.length > 0 ? (
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {types.map(t => {
                const isGenOpen = genFor === t.id;
                const preview   = isGenOpen ? generateRoomNumbers(gen.start, gen.count) : [];
                return (
                  <div key={t.id} style={{
                    background:"#fff",
                    border: isGenOpen ? "1px solid var(--gold)" : "1px solid var(--border)",
                    borderRadius:8, overflow:"hidden",
                  }}>
                    <div className="rt-card-row">
                      <div style={{ minWidth:0 }}>
                        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:18, color:"var(--dark)" }}>
                          {t.name}
                        </div>
                        <div style={{ fontSize:11.5, color:"var(--mid)", marginTop:4, display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" }}>
                          {Number(t.base_price) > 0 && (
                            <span><strong style={{ color:"var(--dark)" }}>GH₵ {Number(t.base_price).toFixed(2)}</strong> /night</span>
                          )}
                          {Number(t.hourly_rate) > 0 && (
                            <span style={{ paddingLeft:8, borderLeft:"1px solid rgba(180,140,80,.25)" }}>
                              <strong style={{ color:"var(--dark)" }}>GH₵ {Number(t.hourly_rate).toFixed(2)}</strong> /hour
                            </span>
                          )}
                          {Number(t.daily_rate) > 0 && (
                            <span style={{ paddingLeft:8, borderLeft:"1px solid rgba(180,140,80,.25)" }}>
                              <strong style={{ color:"var(--dark)" }}>GH₵ {Number(t.daily_rate).toFixed(2)}</strong> /day
                            </span>
                          )}
                          <span style={{ paddingLeft:8, borderLeft:"1px solid rgba(180,140,80,.25)" }}>
                            {t.base_occupancy} guests · {t.room_count} room{t.room_count===1?"":"s"}
                          </span>
                        </div>
                      </div>
                      <div className="rt-actions">
                        <button className="btn-gold"
                                style={{ padding:"6px 12px", fontSize:10.5, letterSpacing:".1em", whiteSpace:"nowrap" }}
                                onClick={() => isGenOpen ? setGenFor(null) : openGen(t)}>
                          {isGenOpen ? "Close" : "+ Generate rooms"}
                        </button>
                        <button className="btn-outline" style={{ padding:"6px 12px", fontSize:11 }}
                                onClick={() => { setEditing(t.id); setForm({ name:t.name, description:t.description || "", base_price:t.base_price, hourly_rate: t.hourly_rate ?? "", daily_rate: t.daily_rate ?? "", base_occupancy:t.base_occupancy }); }}>
                          Edit
                        </button>
                        <button className="btn-outline"
                                style={{ padding:"6px 12px", fontSize:11, color:"#a93226", borderColor:"rgba(192,57,43,.3)" }}
                                onClick={() => remove(t)}>
                          Delete
                        </button>
                      </div>
                    </div>

                    {isGenOpen && (
                      <div style={{
                        padding:"14px 16px 16px",
                        background:"rgba(200,160,90,.06)",
                        borderTop:"1px dashed rgba(200,160,90,.3)",
                      }}>
                        <div style={{ fontSize:12, color:"var(--mid)", marginBottom:10, lineHeight:1.5 }}>
                          Register many <strong style={{ color:"var(--dark)" }}>{t.name}</strong> rooms at once. They all inherit this type's price (GH₵ {Number(t.base_price).toFixed(2)}) and occupancy ({t.base_occupancy}). Edit individual rooms later for one-off changes.
                        </div>
                        <div style={{ display:"grid", gridTemplateColumns:"1.4fr .8fr 1fr", gap:10, marginBottom:10 }}>
                          <div>
                            <div className="f-label">Starting number *</div>
                            <input className="f-input" value={gen.start}
                                   onChange={e => setGen({ ...gen, start:e.target.value })}
                                   placeholder="e.g. 201 · G05 · VVIP-1" />
                          </div>
                          <div>
                            <div className="f-label">How many? *</div>
                            <input type="number" min="1" max="500" className="f-input" value={gen.count}
                                   onChange={e => setGen({ ...gen, count:e.target.value })} />
                          </div>
                          <div>
                            <div className="f-label">Floor (optional)</div>
                            <input className="f-input" value={gen.floor}
                                   onChange={e => setGen({ ...gen, floor:e.target.value })}
                                   placeholder="e.g. 2, G" />
                          </div>
                        </div>

                        {preview.length > 0 && (
                          <div style={{
                            padding:"8px 12px", marginBottom:10,
                            background:"#fff", border:"1px solid var(--border)", borderRadius:6,
                            fontSize:12, color:"var(--mid)",
                          }}>
                            <span style={{ fontSize:10.5, letterSpacing:".12em", textTransform:"uppercase", color:"#6b5a4e", marginRight:8 }}>
                              Will create {preview.length}:
                            </span>
                            <span style={{ color:"var(--dark)", fontWeight:400 }}>
                              {preview.slice(0, 3).join(", ")}
                              {preview.length > 3 ? `, … , ${preview[preview.length-1]}` : ""}
                            </span>
                          </div>
                        )}

                        <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
                          <button className="btn-outline" onClick={() => setGenFor(null)} disabled={genBusy}>
                            Cancel
                          </button>
                          <button className="btn-gold"
                                  onClick={() => submitGen(t)}
                                  disabled={genBusy || preview.length === 0}>
                            {genBusy
                              ? "Creating…"
                              : preview.length > 0
                                ? `Create ${preview.length} ${t.name} room${preview.length === 1 ? "" : "s"}`
                                : "Create rooms"}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div style={{ fontSize:12, color:"var(--mid)", padding:"14px 0 18px", textAlign:"center" }}>
              No room types yet — add your first one below.
            </div>
          )}
          </div>{/* /left pane */}

          {/* Right pane — Add / Edit form, scrolls independently. */}
          <div className="rt-form">
            <div className="f-label" style={{ marginBottom:10 }}>
              {editing ? "Edit room type" : "Add a room type"}
            </div>
            <div className="form-grid">
              <Field label="Name *">
                <input className="f-input" value={form.name}
                       onChange={e => setForm({ ...form, name:e.target.value })}
                       placeholder="e.g. Deluxe Suite" />
              </Field>
              <Field label="Nightly rate (GH₵)">
                <input type="number" min="0" step="0.01" className="f-input" value={form.base_price}
                       onChange={e => setForm({ ...form, base_price:e.target.value })} />
              </Field>
              <Field label="Hourly rate (GH₵)">
                <input type="number" min="0" step="0.01" className="f-input"
                       value={form.hourly_rate ?? ""}
                       placeholder="Optional — used for short walk-ins"
                       onChange={e => setForm({ ...form, hourly_rate:e.target.value })} />
              </Field>
              <Field label="Daily rate (GH₵)">
                <input type="number" min="0" step="0.01" className="f-input"
                       value={form.daily_rate ?? ""}
                       placeholder="Optional — used for whole-day / day-use stays"
                       onChange={e => setForm({ ...form, daily_rate:e.target.value })} />
              </Field>
              <Field label="Base occupancy">
                <input type="number" min="1" className="f-input" value={form.base_occupancy}
                       onChange={e => setForm({ ...form, base_occupancy:e.target.value })} />
              </Field>
              <Field label="Description">
                <input className="f-input" value={form.description}
                       onChange={e => setForm({ ...form, description:e.target.value })}
                       placeholder="Short summary" />
              </Field>
            </div>
            {editing && (
              <label style={{ display:"flex", gap:8, alignItems:"flex-start", fontSize:12, color:"var(--mid)", lineHeight:1.5, margin:"2px 0 12px", cursor:"pointer" }}>
                <input type="checkbox" checked={applyAll} onChange={e => setApplyAll(e.target.checked)} style={{ marginTop:3 }} />
                <span>
                  Update the nightly rate and occupancy on <strong style={{ color:"var(--dark)" }}>all {types.find(t => t.id === editing)?.room_count ?? ""} rooms</strong> of this type,
                  replacing any individual room prices. Untick to keep rooms that have their own price. (Hourly and daily rates always apply to every room.)
                </span>
              </label>
            )}
            <div style={{ display:"flex", gap:8, justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", marginTop:4 }}>
              <div style={{ fontSize:11.5, color: ratesEntered() ? "var(--mid)" : "#a93226", fontWeight: ratesEntered() ? 400 : 500 }}>
                {ratesEntered()
                  ? "✓ At least one rate entered."
                  : "⚠️ Enter at least one rate — nightly, hourly, or daily."}
              </div>
              <div style={{ display:"flex", gap:8 }}>
                {editing && (
                  <button className="btn-outline" onClick={() => { setEditing(null); setForm(EMPTY_TYPE); setApplyAll(true); }}>
                    Cancel edit
                  </button>
                )}
                <button className="btn-gold" onClick={save}
                  disabled={busy || !form.name.trim() || !ratesEntered()}
                  title={!ratesEntered() ? "Enter at least one rate" : undefined}>
                  {busy ? "Saving…" : editing ? "Save changes" : "Add type"}
                </button>
              </div>
            </div>
          </div>{/* /right pane */}
          </div>{/* /rt-split */}
        </div>
      </div>
    </div>
  );
}

// ── Confirm dialog ─────────────────────────────────────────────────────────
function ConfirmDialog({ title, body, confirmLabel, onCancel, onConfirm }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box" style={{ width:"min(440px,92vw)" }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onCancel}>✕</button>
        </div>
        <div className="modal-body">
          <div style={{ fontSize:13, color:"#4a3828", fontWeight:300, lineHeight:1.6, marginBottom:18 }}>
            {body}
          </div>
          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button className="btn-outline" onClick={onCancel}>Cancel</button>
            <button className="btn-gold" style={{ background:"#a93226", color:"#fff" }}
                    onClick={onConfirm}>
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
